import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, ShoppingCart, X, Menu } from 'lucide-react';
import PremiumMenuModal from './PremiumMenuModal';
import SeoHead from './SeoHead';
import FAQSection from './FAQSection';

const faqItems = [
  { q: 'Do you offer spit braai hire in Pretoria?', a: 'Yes, we provide spit braai hire in Pretoria and across Gauteng, including Centurion and Midrand.' },
  { q: 'Can I hire with catering included?', a: 'Yes, you can choose machine-only hire or a full package with catering options.' },
  { q: 'How far in advance should I book?', a: 'Weekend bookings fill fast, so we suggest booking early for best date availability.' },
  { q: 'Do you support corporate events in Johannesburg?', a: 'Yes. We support private and corporate spit braai events across Gauteng.' },
  { q: 'How do I request a quote near me?', a: 'Select your package and send your booking list via WhatsApp in one step.' },
];

const SpitBraaiPage = () => {
  const [selectedPackages, setSelectedPackages] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [showMenuModal, setShowMenuModal] = useState(false);

  // Load saved packages from localStorage on component mount
  useEffect(() => {
    try {
      const savedPackages = localStorage.getItem('ubuntu-events-management-spitbraai-packages');
      if (savedPackages) {
        setSelectedPackages(JSON.parse(savedPackages));
      }
    } catch (error) {
      console.warn('Failed to load from localStorage:', error);
    }
  }, []);

  // Save packages to localStorage whenever selectedPackages changes
  useEffect(() => {
    try {
      localStorage.setItem('ubuntu-events-management-spitbraai-packages', JSON.stringify(selectedPackages));
    } catch (error) {
      console.warn('Failed to save packages to localStorage:', error);
    }
  }, [selectedPackages]);

  const spitBraaiPackages = [
    {
      id: 'standard',
      name: 'Standard Hire',
      description: 'Spit braai machine only (no meat or staff)',
      price: 1500,
      image: '/vintage-spit-braai-machine.jpg',
      features: ['Spit braai machine', 'Basic setup instructions', 'Charcoal not included']
    },
    {
      id: 'fullservice',
      name: 'Full Service',
      description: 'Includes machine, setup, and on-site braai master',
      price: 4500,
      image: 'https://images.unsplash.com/photo-1529692236671-f1f6cf9683ba?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
      features: ['Spit braai machine', 'Professional setup', 'On-site braai master', 'Charcoal included']
    },
    {
      id: 'premium',
      name: 'Premium Package',
      description: 'Full service + meat (lamb or beef) for up to 50 guests',
      price: 6500,
      image: '/premium-package.jpg',
      features: ['Everything in Full Service', 'Choice of lamb or beef', 'Serves up to 50 guests', 'Complete meal solution']
    }
  ];

  const meatImages = [
    '/spit-braai-image.jpg',
    '/spitbraai-picture-2.jpg',
    '/our-spitbraai-in-action-2.jpg',
    '/our-spitbraai-in-action-3.jpg'
  ];


  const addToCart = (packageItem) => {
    const existingPackage = selectedPackages.find(selected => selected.id === packageItem.id);
    if (existingPackage) {
      setSelectedPackages(selectedPackages.map(selected => 
        selected.id === packageItem.id 
          ? { ...selected, quantity: selected.quantity + 1 }
          : selected
      ));
    } else {
      setSelectedPackages([...selectedPackages, { ...packageItem, quantity: 1 }]);
    }
  };

  const removeFromCart = (packageId) => {
    setSelectedPackages(selectedPackages.filter(pkg => pkg.id !== packageId));
  };

  const updateQuantity = (packageId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(packageId);
    } else {
      setSelectedPackages(selectedPackages.map(pkg => 
        pkg.id === packageId 
          ? { ...pkg, quantity: newQuantity }
          : pkg
      ));
    }
  };

  const getTotalPrice = () => {
    return selectedPackages.reduce((total, pkg) => total + (pkg.price * pkg.quantity), 0);
  };

  const hasSelection = selectedPackages.length > 0;

  const buildWhatsAppListMessage = () => {
    const lines = selectedPackages.map((pkg, index) => {
      const lineTotal = pkg.price * pkg.quantity;
      return `${index + 1}. ${pkg.name}\n   ${pkg.quantity} × R${pkg.price} = R${lineTotal}`;
    });

    return `Hi UBUNTU EVENTS MANAGEMENT,

I'd like to book the following spit braai package(s):

${lines.join('\n\n')}

────────────
Total: R${getTotalPrice().toFixed(2)}
────────────

Please confirm availability and final pricing.`;
  };

  const sendWhatsAppMessage = () => {
    if (selectedPackages.length === 0) {
      alert('Please select at least one spit braai package before sending your booking.');
      return;
    }

    try {
      const message = buildWhatsAppListMessage();
      const encodedMessage = encodeURIComponent(message);
      const whatsappUrl = `https://wa.me/27663286231?text=${encodedMessage}`;
      if (whatsappUrl.length > 8000) {
        window.alert(
          'Your selection list is very long for one WhatsApp link. Please reduce items in Review.'
        );
        setShowCart(true);
        return;
      }
      window.open(whatsappUrl, '_blank');
    } catch (error) {
      console.error('Failed to open WhatsApp:', error);
      alert('Failed to open WhatsApp. Please try again or contact us directly.');
    }
  };

  return (
    <div className={`min-h-screen ${hasSelection ? 'pb-32 sm:pb-28' : ''}`}>
      <SeoHead
        title="Spit Braai Hire Pretoria | Ubuntu Enterprises"
        description="Spit braai hire in Pretoria and Gauteng with package options and WhatsApp booking."
        canonicalPath="/spitbraai"
        faqSchema={faqItems}
      />
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('/spit-braai-image.jpg')"
          }}
        />
        
        {/* Overlay */}
        <div className="hero-overlay">
          <div className="text-center text-white px-4 max-w-4xl mx-auto">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-5xl md:text-7xl font-playfair font-bold mb-6"
            >
              Spit Braai Rental for Your Next Event
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl md:text-2xl mb-8 font-light"
            >
              Bring the fire and flavor to your celebration with our professional spit braai setup
            </motion.p>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-playfair font-bold text-gray-800 mb-8">
              Why Choose Our Spit Braai?
            </h2>
            
            <div className="max-w-4xl mx-auto">
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
                Our spit braai service is perfect for weddings, birthdays, or any outdoor celebration. 
                We provide a complete setup that includes the machine, charcoal, and our team's expertise 
                to ensure your meat is cooked to perfection.
              </p>
            </div>
          </motion.div>

          {/* Meat Gallery Section */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <h3 className="text-3xl font-playfair font-bold text-gray-800 mb-8 text-center">
              Our Spit Braai in Action
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {meatImages.map((image, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="group"
                >
                  <div className="aspect-w-16 aspect-h-12 overflow-hidden rounded-lg shadow-lg">
                    <img
                      src={image}
                      alt={`Spit braai hire Gauteng showcase ${index + 1}`}
                      className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                </motion.div>
              ))}
            </div>
            
            <div className="text-center mt-8">
              <p className="text-lg text-gray-600 italic">
                "Nothing beats the authentic taste of meat slow-roasted on our professional spit braai"
              </p>
            </div>
          </motion.div>

          {/* Rental Options */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h3 className="text-3xl font-playfair font-bold text-gray-800 mb-12 text-center">
              Rental Packages
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {spitBraaiPackages.map((packageItem, index) => (
                <motion.div
                  key={packageItem.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="card group hover:shadow-2xl"
                >
                  <div className="aspect-w-16 aspect-h-9 mb-6 overflow-hidden rounded-lg">
                    <img
                      src={packageItem.image}
                      alt={`${packageItem.name} spit braai package Gauteng`}
                      className="w-full h-40 sm:h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  
                  <h4 className="text-2xl font-playfair font-semibold text-gray-800 mb-3">
                    {packageItem.name}
                  </h4>
                  
                  <p className="text-gray-600 mb-4">
                    {packageItem.description}
                  </p>

                  <ul className="text-sm text-gray-600 mb-6 space-y-1">
                    {packageItem.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center">
                        <span className="text-gold mr-2">✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <div className="flex justify-between items-center mb-6">
                    <span className="text-3xl font-bold text-gold">
                      {packageItem.id === 'premium' ? 'from R' : 'R'}{packageItem.price}
                    </span>
                    <span className="text-sm text-gray-500">per day</span>
                  </div>
                  
                  <div className="space-y-2">
                    <button
                      onClick={() => addToCart(packageItem)}
                      className="w-full btn-primary text-sm py-3"
                    >
                      Book Spit Braai
                    </button>
                    {packageItem.id === 'premium' && (
                      <button
                        onClick={() => setShowMenuModal(true)}
                        className="w-full btn-secondary text-sm py-3 flex items-center justify-center space-x-2"
                      >
                        <Menu size={16} />
                        <span>Select Your Menu</span>
                      </button>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {hasSelection && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="fixed bottom-0 left-0 right-0 z-40 border-t border-black/15 bg-[#f5f0e6]/95 backdrop-blur-sm shadow-[0_-4px_24px_rgba(0,0,0,0.08)] pb-[max(0.75rem,env(safe-area-inset-bottom))]"
        >
          <div className="max-w-6xl mx-auto px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <button
              type="button"
              onClick={() => setShowCart(true)}
              className="flex items-center gap-3 text-left text-black min-w-0 flex-1"
              aria-label="Review spit braai selections"
            >
              <span className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#2a2a2a] text-[#f5f0e6]">
                <ShoppingCart size={20} />
                <span className="absolute -top-1 -right-1 flex h-5 min-w-[1.25rem] px-1 items-center justify-center rounded-full bg-red-600 text-[10px] font-bold text-white">
                  {selectedPackages.length}
                </span>
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-black">
                  {selectedPackages.length} selection{selectedPackages.length !== 1 ? 's' : ''} · R{getTotalPrice().toFixed(2)}
                </span>
                <span className="block text-xs text-black/55 truncate">
                  Tap to review, edit quantities, or send the full list to WhatsApp
                </span>
              </span>
            </button>
            <div className="flex gap-2 shrink-0 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => setShowCart(true)}
                className="flex-1 sm:flex-none rounded-lg border border-black/20 bg-white/60 px-4 py-2.5 text-sm font-semibold text-black hover:bg-white/90"
              >
                Review
              </button>
              <button
                type="button"
                onClick={sendWhatsAppMessage}
                className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-green-700"
              >
                <MessageCircle size={18} />
                WhatsApp
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Cart Modal */}
      {showCart && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#f5f0e6] rounded-t-2xl sm:rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto border border-black/10 shadow-xl"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-black">Your spit braai booking</h3>
              <button
                onClick={() => setShowCart(false)}
                className="text-black/50 hover:text-black"
                aria-label="Close"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
              {/* Packages */}
              {selectedPackages.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Rental Packages</h4>
                  {selectedPackages.map(pkg => (
                    <div key={pkg.id} className="flex justify-between items-center border-b pb-2 mb-2">
                      <div className="flex-1">
                        <h4 className="font-semibold text-sm text-black">{pkg.name}</h4>
                        <p className="text-xs text-black/60">R{pkg.price} per day</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => updateQuantity(pkg.id, pkg.quantity - 1)}
                          className="w-7 h-7 rounded-full bg-black/10 flex items-center justify-center text-sm text-black"
                        >
                          −
                        </button>
                        <span className="w-7 text-center text-sm font-medium">{pkg.quantity}</span>
                        <button
                          onClick={() => updateQuantity(pkg.id, pkg.quantity + 1)}
                          className="w-7 h-7 rounded-full bg-black/10 flex items-center justify-center text-sm text-black"
                        >
                          +
                        </button>
                      </div>
                      <div className="text-right ml-4">
                        <span className="font-semibold text-sm">R{pkg.price * pkg.quantity}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

            </div>

            <p className="text-xs text-black/55 mb-3">
              The numbered list below is sent exactly as shown to WhatsApp.
            </p>
            <div className="rounded-lg border border-black/10 bg-white/40 p-3 mb-4 max-h-40 overflow-y-auto text-xs text-black/80 whitespace-pre-wrap font-mono leading-relaxed">
              {buildWhatsAppListMessage()}
            </div>
            
            <div className="border-t border-black/15 pt-4 mb-4">
              <div className="flex justify-between text-lg font-bold text-black">
                <span>Total:</span>
                <span>R{getTotalPrice().toFixed(2)}</span>
              </div>
            </div>
            
            <button
              onClick={sendWhatsAppMessage}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg flex items-center justify-center space-x-2"
            >
              <MessageCircle size={20} />
              <span>Send full list to WhatsApp</span>
            </button>
          </motion.div>
        </div>
      )}

      <PremiumMenuModal
        isOpen={showMenuModal}
        onClose={() => setShowMenuModal(false)}
      />
      <FAQSection title="Spit Braai FAQs" faqs={faqItems} />
    </div>
  );
};

export default SpitBraaiPage;
